import React, { useCallback, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { cn } from '@/lib/utils';
import { ArchiveCoverImage } from './ArchiveCoverImage';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';
import { getBackendBaseUrl } from '../../utils/apiBase';
import { humanizeError } from '../../utils/errorHandling';
import {
  COVER_ADJUST_GUIDANCE,
  COVER_SOFT_IMAGE_WARNING,
  DEFAULT_COVER_DISPLAY,
  isCoverImageSoft,
  normalizeCoverDisplay,
} from '../../utils/coverDisplay';
import { saveTreeCoverDisplay } from '../../utils/treeCoverUpload';

const BACKEND_URL = getBackendBaseUrl();

function SliderRow({ id, label, value, min, max, step, onChange, formatValue }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor={id} className="text-xs font-medium text-muted-foreground">
          {label}
        </Label>
        <span className="text-[11px] tabular-nums text-muted-foreground/80">{formatValue(value)}</span>
      </div>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="h-1.5 w-full cursor-pointer accent-[#8b7355]"
      />
    </div>
  );
}

/**
 * Simple post-upload cover framing: fit/fill, position, light zoom.
 */
export function CoverAdjustPanel({
  treeId,
  coverImageUrl,
  coverDisplay,
  coverImageWidth,
  coverImageHeight,
  onSaved,
  className,
}) {
  const src = coverImageUrl ? resolveAssetUrl(BACKEND_URL, coverImageUrl) : null;
  const [draft, setDraft] = useState(() => normalizeCoverDisplay(coverDisplay));
  const [saving, setSaving] = useState(false);
  const [measuredSoft, setMeasuredSoft] = useState(false);

  useEffect(() => {
    setDraft(normalizeCoverDisplay(coverDisplay));
  }, [coverDisplay, coverImageUrl]);

  const storedSoft = isCoverImageSoft(coverImageWidth, coverImageHeight);
  const showSoftWarning = storedSoft || measuredSoft;

  const patchDraft = useCallback((partial) => {
    setDraft((prev) => normalizeCoverDisplay({ ...prev, ...partial }));
  }, []);

  const handleSave = async () => {
    if (!treeId) return;
    setSaving(true);
    try {
      const saved = await saveTreeCoverDisplay(treeId, draft);
      toast.success('Cover framing saved.');
      onSaved?.(saved);
    } catch (error) {
      toast.error(humanizeError(error) || 'Unable to save cover adjustments.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setDraft({ ...DEFAULT_COVER_DISPLAY });
  };

  if (!src) return null;

  return (
    <div
      className={cn(
        'mt-4 space-y-4 rounded-xl border border-[hsl(38_14%_86%)] bg-[hsl(42_28%_99%)]/80 p-4',
        className
      )}
      data-testid="cover-adjust-panel"
    >
      <div>
        <h4 className="font-serif text-sm font-medium text-[hsl(24_12%_22%)]">Adjust cover</h4>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{COVER_ADJUST_GUIDANCE}</p>
      </div>

      <div className="overflow-hidden rounded-lg border border-[hsl(38_14%_88%)]">
        <div className="relative aspect-[16/5] min-h-[72px] bg-[hsl(42_26%_96%)]">
          <ArchiveCoverImage src={src} alt="Cover preview" coverDisplay={draft} showGradient={false} />
        </div>
      </div>

      {showSoftWarning ? (
        <p className="rounded-lg border border-amber-200/80 bg-amber-50/90 px-3 py-2 text-xs leading-relaxed text-amber-900/90">
          {COVER_SOFT_IMAGE_WARNING}
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2">
        {[
          { id: 'fill', label: 'Fill cover' },
          { id: 'fit', label: 'Fit inside cover' },
        ].map(({ id, label }) => (
          <button
            key={id}
            type="button"
            onClick={() => patchDraft({ fit_mode: id })}
            className={cn(
              'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
              draft.fit_mode === id
                ? 'border-[#c9a961]/50 bg-[#fffaf3] text-foreground'
                : 'border-border/60 bg-background/60 text-muted-foreground hover:border-[#c9a961]/30'
            )}
            data-testid={`cover-fit-${id}`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        <SliderRow
          id="cover-pos-x"
          label="Position left / right"
          value={draft.pos_x}
          min={0}
          max={100}
          step={1}
          onChange={(v) => patchDraft({ pos_x: v })}
          formatValue={(v) => (v < 45 ? 'Left' : v > 55 ? 'Right' : 'Center')}
        />
        <SliderRow
          id="cover-pos-y"
          label="Position up / down"
          value={draft.pos_y}
          min={0}
          max={100}
          step={1}
          onChange={(v) => patchDraft({ pos_y: v })}
          formatValue={(v) => (v < 45 ? 'Up' : v > 55 ? 'Down' : 'Middle')}
        />
        <SliderRow
          id="cover-zoom"
          label="Zoom"
          value={draft.zoom}
          min={0.85}
          max={1.35}
          step={0.01}
          onChange={(v) => patchDraft({ zoom: v })}
          formatValue={(v) => `${Math.round(v * 100)}%`}
        />
      </div>

      <div className="flex flex-wrap gap-2 pt-1">
        <Button
          type="button"
          size="sm"
          className="rounded-full bg-[#1c1917] text-[#faf7f2] hover:bg-[#2c2824]"
          disabled={saving}
          onClick={handleSave}
          data-testid="cover-adjust-save-btn"
        >
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" /> : null}
          Save framing
        </Button>
        <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={handleReset}>
          Reset
        </Button>
      </div>

      {/* Measure natural size when stored dimensions missing */}
      {!coverImageWidth || !coverImageHeight ? (
        <img
          src={src}
          alt=""
          className="hidden"
          onLoad={(e) => {
            const img = e.currentTarget;
            if (isCoverImageSoft(img.naturalWidth, img.naturalHeight)) {
              setMeasuredSoft(true);
            }
          }}
        />
      ) : null}
    </div>
  );
}
